import { useEffect, useMemo, useState } from 'react'

import { AppError, toastAppError } from '@/lib/Errors/AppError.ts'
import type {
    RouteErrorCategory,
    RouteErrorDetails,
    RouteErrorLogicReturn,
    UseRouteErrorLogicProps,
} from '@/shared/RouteErrorComponent/Types/routeError.types.ts'

function getErrorRecord(error: unknown): Record<string, unknown> | undefined {
    return typeof error === 'object' && error !== null
        ? (error as Record<string, unknown>)
        : undefined
}

function getStatusCode(error: unknown) {
    if (typeof Response !== 'undefined' && error instanceof Response) {
        return error.status
    }

    const errorRecord = getErrorRecord(error)
    const statusCode = errorRecord?.statusCode ?? errorRecord?.status

    return typeof statusCode === 'number' ? statusCode : undefined
}

function getErrorName(error: unknown) {
    if (error instanceof Error && error.name) {
        return error.name
    }

    const name = getErrorRecord(error)?.name
    return typeof name === 'string' && name ? name : 'Error'
}

function getErrorMessage(error: unknown) {
    if (typeof error === 'string' && error) {
        return error
    }

    if (typeof Response !== 'undefined' && error instanceof Response) {
        return `${error.status} ${error.statusText}`.trim()
    }

    return AppError.getMessage(error)
}

function getErrorCause(error: unknown) {
    const cause = error instanceof Error ? error.cause : undefined

    if (cause instanceof Error) {
        return cause.message
    }

    if (typeof cause === 'string') {
        return cause
    }

    if (!cause) {
        return undefined
    }

    try {
        return JSON.stringify(cause)
    } catch {
        return String(cause)
    }
}

function extractErrorPath(stack?: string) {
    if (!stack) {
        return undefined
    }

    const sourceLine = stack.split('\n').find((line) => {
        return (
            (line.includes('/') || line.includes('\\')) &&
            !line.includes('node_modules')
        )
    })

    if (!sourceLine) {
        return undefined
    }

    const parenthesizedPath = sourceLine.match(/\((.+)\)/)?.[1]
    const rawPath = parenthesizedPath ?? sourceLine.trim().replace(/^at\s+/, '')

    return rawPath
        .replace(/https?:\/\/[^/]+\//, '')
        .split('?')[0]
        .replace(/:\d+:\d+$/, '')
}

function getErrorCategory(
    message: string,
    statusCode?: number,
): RouteErrorCategory {
    const normalizedMessage = message.toLowerCase()

    if (
        normalizedMessage.includes('failed to fetch dynamically imported') ||
        normalizedMessage.includes('failed to load url') ||
        normalizedMessage.includes('err_load_url') ||
        normalizedMessage.includes('loading chunk')
    ) {
        return 'moduleLoad'
    }

    if (
        normalizedMessage.includes('failed to fetch') ||
        normalizedMessage.includes('networkerror') ||
        normalizedMessage.includes('network request failed') ||
        normalizedMessage.includes('load failed')
    ) {
        return 'network'
    }

    if (statusCode === 401 || statusCode === 403) {
        return 'authorization'
    }

    if (statusCode === 404) {
        return 'notFound'
    }

    if (statusCode && statusCode >= 500) {
        return 'server'
    }

    return 'generic'
}

function createTechnicalDetails(
    details: Omit<RouteErrorDetails, 'technicalDetails'>,
) {
    return [
        `Name: ${details.errorName}`,
        details.statusCode ? `HTTP-Status: ${details.statusCode}` : undefined,
        `Meldung: ${details.message}`,
        details.errorPath ? `Fundort: ${details.errorPath}` : undefined,
        details.cause ? `Ursache: ${details.cause}` : undefined,
        details.stack ? `\nStack Trace:\n${details.stack}` : undefined,
        details.componentStack
            ? `\nComponent Stack:\n${details.componentStack}`
            : undefined,
    ]
        .filter(Boolean)
        .join('\n')
}

function getRouteErrorDetails(
    error: unknown,
    componentStack?: string,
): RouteErrorDetails {
    const message = getErrorMessage(error)
    const statusCode = getStatusCode(error)
    const stack = error instanceof Error ? error.stack : undefined
    const detailsWithoutTechnicalText = {
        category: getErrorCategory(message, statusCode),
        cause: getErrorCause(error),
        componentStack,
        errorName: getErrorName(error),
        errorPath: extractErrorPath(stack),
        message,
        stack,
        statusCode,
    }

    return {
        ...detailsWithoutTechnicalText,
        technicalDetails: createTechnicalDetails(detailsWithoutTechnicalText),
    }
}

export default function useRouteErrorLogic({
    error,
    info,
    reset,
}: UseRouteErrorLogicProps): RouteErrorLogicReturn {
    const [copied, setCopied] = useState(false)
    const [isTechnicalDetailsOpen, setIsTechnicalDetailsOpen] = useState(false)
    const details = useMemo(
        () => getRouteErrorDetails(error, info?.componentStack),
        [error, info?.componentStack],
    )

    useEffect(() => {
        toastAppError(error)
    }, [error])

    function handleRetry() {
        reset()
    }

    function handleReload() {
        window.location.reload()
    }

    async function handleCopyTechnicalDetails() {
        try {
            await navigator.clipboard.writeText(details.technicalDetails)
            setCopied(true)
            window.setTimeout(() => setCopied(false), 2000)
        } catch {
            setCopied(false)
        }
    }

    return {
        state: {
            copied,
            details,
            isTechnicalDetailsOpen,
        },
        handler: {
            handleCopyTechnicalDetails,
            handleReload,
            handleRetry,
        },
        setter: {
            setIsTechnicalDetailsOpen,
        },
    }
}
